/**
 * Doctor prescriptions list and edit entry point.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { prescriptionAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const DoctorPrescriptions = () => {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadPrescriptions = useCallback(async () => {
    try {
      const res = await prescriptionAPI.list();
      setPrescriptions(Array.isArray(res.data) ? res.data : (res.data?.prescriptions || []));
    } catch (err) {
      toast.error(err?.message || 'Failed to load prescriptions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  const filteredPrescriptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return prescriptions;

    return prescriptions.filter((item) => {
      const medicationNames = (Array.isArray(item.medications) ? item.medications : [])
        .map((med) => med?.name || '')
        .join(' ')
        .toLowerCase();

      return [
        item.patient_name,
        item.diagnosis,
        item.instructions,
        medicationNames,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle));
    });
  }, [prescriptions, search]);

  return (
    <Layout title="Prescriptions" role="doctor">
      <div className="p-4 pb-24 space-y-4">
        <div className="card space-y-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">Issued Prescriptions</p>
            <p className="text-xs text-gray-500 mt-1">
              Review every prescription you have sent, then open any one to edit and sync changes to the patient.
            </p>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field"
            placeholder="Search by patient, diagnosis, or medication"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="skeleton h-28 rounded-2xl"></div>
            ))}
          </div>
        ) : filteredPrescriptions.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-medium">No prescriptions yet</p>
            <p className="text-sm mt-1">Write prescriptions after consultations and they will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPrescriptions.map((item) => {
              const medications = Array.isArray(item.medications) ? item.medications : [];
              return (
                <div key={item.id} className="card space-y-3 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{item.patient_name || 'Patient'}</p>
                      <p className="text-sm text-gray-500 mt-1">{item.diagnosis || 'Prescription'}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Issued {item.created_at ? new Date(item.created_at).toLocaleString() : 'recently'}
                      </p>
                      {item.updated_at && item.updated_at !== item.created_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Updated {new Date(item.updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {item.status || 'active'}
                    </span>
                  </div>

                  {medications.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {medications.slice(0, 4).map((med, index) => (
                        <span key={`${item.id}-${index}`} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                          {med?.name || `Medication ${index + 1}`}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/doctor/prescriptions/write?prescription=${item.id}`)}
                      className="px-3 py-2 bg-accent-500 text-white text-sm font-medium rounded-lg hover:bg-accent-600 transition-colors"
                    >
                      Edit Prescription
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorPrescriptions;
